import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Link,
  Select,
  Text,
} from '@chakra-ui/react';
import {
  articlesApi,
  CreateArticleNodeValue,
} from '@texas/api/endpoints/articlesApi';
import { MatrixAxis } from '@texas/api/endpoints/compositionGroup/compositionGroupMatrixApi';
import { FileLink } from '@texas/api/endpoints/filesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { clientEndpoints } from '@texas/clientEndpoints';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { FileDropzone } from '@texas/components/shared/dropzone/FileDropzone';
import { createArticleStyles as styles } from '@texas/resources/theme/styles/entityWorkspace';
import { contentTypes } from '@texas/utils/helpers/filesHelper';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateArticle } from './useCreateArticle';
import { ItemCodeAvailabilityStatus } from './ItemCodeAvailabilityStatus';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { CreateArticleMatrix } from './CreateArticleMatrix';
import { Icons } from '@texas/components/shared/Icons';
import { ArticleFieldWarning } from './ArticleFieldWarning';

export interface ProposedArticleOptions {
  productGroupId?: number;
  nodeXValues?: CreateArticleNodeValue[];
  nodeYValues?: CreateArticleNodeValue[];
  colorAxis?: number | null;
  layout?: FileLink;
  importErp?: boolean;
  masterBranchId?: number;
  uploading?: boolean;
}
interface Props {
  itemCode: string;
  name: string;
  isExpanded: boolean;
  onToggle: () => void;
}
export function ProposedArticleCard({
  itemCode,
  name,
  isExpanded,
  onToggle,
}: Props) {
  const {
    availability,
    setValue,
    getValues,
    creationTargets,
    proposals,
    resources,
    applyConfigurationToAll,
  } = useCreateArticle();
  const proposal = proposals.find((target) => target.itemCode === itemCode)!;
  const {
    template,
    xNode,
    yNode,
    warnings,
    invalidLength,
    unavailableInTexas,
  } = proposal;
  const skipped = !creationTargets.some(
    (target) => target.itemCode === itemCode,
  );
  const hasMultipleArticles =
    creationTargets.filter((target) => !target.unavailableInTexas).length > 1;
  const options = proposal.options;
  const erpConflict = availability.data?.[itemCode]?.errorType === 'InUseByErp';
  const hasMatrix = !unavailableInTexas && !erpConflict && !!(xNode ?? yNode);
  const onChange = (value: Partial<ProposedArticleOptions>) => {
    if (unavailableInTexas) return;
    setValue('options', {
      ...getValues('options'),
      [itemCode]: { ...getValues('options')[itemCode], ...value },
    });
  };
  const onApplyToAll = (layout: FileLink) =>
    setValue('options', {
      ...getValues('options'),
      ...Object.fromEntries(
        creationTargets
          .filter((target) => !target.unavailableInTexas)
          .map((target) => [
            target.itemCode,
            {
              ...getValues('options')[target.itemCode],
              layout,
            },
          ]),
      ),
    });
  const { t } = useTranslation();
  const panelId = useId();
  const missingFields = Object.values(warnings).filter(
    (warning) => warning.isMandatory && warning.warning,
  ).length;
  const productGroup = resources.products.data.find(
    (product) => product.id === options.productGroupId,
  )?.value;
  const summary = [
    productGroup,
    hasMatrix && yNode
      ? `${yNode.name}: ${options.nodeYValues?.length ?? 0}`
      : undefined,
    hasMatrix && xNode
      ? `${xNode.name}: ${options.nodeXValues?.length ?? 0}`
      : undefined,
    options.layout?.originalName,
    options.importErp ? t('article.create.importErp') : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
  const ready =
    !skipped &&
    !invalidLength &&
    missingFields === 0 &&
    !options.uploading &&
    !availability.loading &&
    !availability.error &&
    (availability.data?.[itemCode]?.isValid ||
      (erpConflict && options.importErp));
  const [replacing, setReplacing] = useState(false);
  const uploads = useFileUploads((layout) => {
    onChange({ layout, uploading: false });
    setReplacing(false);
  });
  const {
    data: branches,
    refetch,
    error,
    loading,
  } = useApiResource(articlesApi.getImportBranches);
  useEffect(() => {
    if (erpConflict && !skipped) void refetch(itemCode);
  }, [erpConflict, skipped, itemCode, refetch]);
  return (
    <Box sx={styles.proposed} role="group" aria-label={itemCode}>
      <Box sx={styles.proposedCardHeader}>
        <Button
          variant="unstyled"
          sx={styles.proposedToggle}
          onClick={onToggle}
          isDisabled={skipped}
          aria-expanded={!skipped && isExpanded}
          aria-controls={skipped ? undefined : panelId}
        >
          {isExpanded && !skipped ? (
            <Icons.ChevronUp aria-hidden={true} />
          ) : (
            <Icons.ChevronDown aria-hidden={true} />
          )}
          <Box sx={styles.proposedTitle}>
            <Text>
              {itemCode}
              {name ? ` · ${name}` : ''}
            </Text>
            {!isExpanded && summary && (
              <Text sx={styles.proposedSummary}>{summary}</Text>
            )}
          </Box>
        </Button>
        <Box sx={styles.proposedCardActions}>
          {!skipped && !unavailableInTexas && (missingFields > 0 || ready) && (
            <Badge
              colorScheme={ready ? 'green' : 'orange'}
              sx={styles.proposedReadiness}
            >
              {ready
                ? t('article.create.configurationReady')
                : t('article.create.fieldsRemaining', { count: missingFields })}
            </Badge>
          )}
          {!skipped && !unavailableInTexas && hasMultipleArticles && (
            <Menu>
              <MenuButton
                aria-label={t('article.create.copyToOthers')}
                title={t('article.create.copyToOthers')}
                as={Button}
                variant="outline"
                size="sm"
                rightIcon={<Icons.ChevronDown />}
              >
                <Box as="span" display={{ base: 'none', '2xl': 'inline' }}>
                  {t('article.create.copyToOthers')}
                </Box>
                <Icons.ContentCopy
                  display={{ base: 'block', '2xl': 'none' }}
                  aria-hidden={true}
                />
              </MenuButton>
              <MenuList>
                <MenuItem
                  onClick={() => applyConfigurationToAll(itemCode)}
                  isDisabled={
                    !template ||
                    !options.productGroupId ||
                    availability.loading ||
                    !availability.data ||
                    !!availability.error
                  }
                >
                  {t(
                    hasMatrix
                      ? 'article.create.copyConfiguration'
                      : 'article.create.copyProductGroup',
                  )}
                </MenuItem>
                <MenuItem
                  onClick={() => options.layout && onApplyToAll(options.layout)}
                  isDisabled={!options.layout || !!options.uploading}
                >
                  {t('article.create.copyLayout')}
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </Box>
        <Box sx={styles.proposedAvailability} aria-live="polite">
          {skipped && <Text>{t('article.create.skipped')}</Text>}
          <ItemCodeAvailabilityStatus
            loading={availability.loading}
            validationFailed={!!availability.error}
            result={availability.data?.[itemCode]}
          />
          {unavailableInTexas && (
            <Text mt={2}>
              {t(
                skipped
                  ? 'article.create.skippedUnavailableArticle'
                  : 'article.create.unavailableArticle',
                { itemCode },
              )}
            </Text>
          )}
          {invalidLength && <Text>{t('article.copy.invalidLength')}</Text>}
        </Box>
      </Box>
      {!skipped && (
        <Box
          as="fieldset"
          disabled={unavailableInTexas}
          borderInline={0}
          borderBottom={0}
          m={0}
          id={panelId}
          sx={{
            ...styles.proposedContent,
            display: isExpanded ? 'flex' : 'none',
          }}
        >
          <Box sx={styles.proposedFields}>
            <FormControl
              isRequired={!unavailableInTexas}
              isDisabled={unavailableInTexas}
              isInvalid={!unavailableInTexas && !!warnings.productGroup.warning}
              id={warnings.productGroup.targetId}
            >
              <TexasFormLabel>{t('general.productGroup')}</TexasFormLabel>
              <Select
                aria-label={`${t('general.productGroup')} · ${itemCode}`}
                value={options.productGroupId ?? ''}
                placeholder={t('general.select')}
                onChange={(event) =>
                  onChange({ productGroupId: Number(event.target.value) })
                }
              >
                {resources.products.data
                  .filter((item) => !item.archived)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.value}
                    </option>
                  ))}
              </Select>
              {!unavailableInTexas && (
                <ArticleFieldWarning {...warnings.productGroup} />
              )}
            </FormControl>
            <FormControl minW={0}>
              <TexasFormLabel>
                {t('article.create.optionalLayout')}
              </TexasFormLabel>
              <Box sx={styles.stack}>
                {options.layout && (
                  <Box sx={styles.layoutFile}>
                    <Text sx={styles.layoutPdf}>PDF</Text>
                    <Link
                      sx={styles.layoutFilename}
                      href={clientEndpoints.downloadFile(
                        options.layout.identifier,
                      )}
                      isExternal={true}
                    >
                      {options.layout.originalName}
                    </Link>
                    <Link
                      href={clientEndpoints.downloadFile(
                        options.layout.identifier,
                      )}
                      isExternal={true}
                    >
                      {t('general.preview')}
                    </Link>
                    <Button
                      variant="texas-link-light"
                      onClick={() => setReplacing(true)}
                    >
                      {t('general.replace')}
                    </Button>
                    <Button
                      variant="texas-link-light"
                      onClick={() => onChange({ layout: undefined })}
                    >
                      {t('general.remove')}
                    </Button>
                  </Box>
                )}
                {(!options.layout || replacing) && (
                  <FileDropzone
                    isDisabled={unavailableInTexas}
                    compact={{
                      description: t('article.create.dropLayout'),
                      buttonLabel: t('article.create.uploadLayout'),
                    }}
                    accept={{ [contentTypes.pdf]: ['.pdf'] }}
                    multiple={false}
                    onUpload={async (files) => {
                      onChange({ uploading: true });
                      try {
                        await uploads.uploadFiles(files, null);
                      } finally {
                        onChange({ uploading: false });
                      }
                    }}
                    isUploading={uploads.isUploading}
                    uploadFailed={uploads.uploadFailed}
                    fileUploads={uploads.fileUploads}
                    removeFileUpload={(file) => {
                      uploads.removeFileUpload(file);
                      onChange({ uploading: false });
                    }}
                  />
                )}
              </Box>
            </FormControl>
          </Box>
          {hasMatrix && (
            <Box sx={styles.proposedFields}>
              {yNode && (
                <CreateArticleMatrix
                  node={yNode}
                  values={options.nodeYValues ?? []}
                  onChange={(nodeYValues) => onChange({ nodeYValues })}
                  max={62}
                  isColorAxis={options.colorAxis === MatrixAxis.Rows}
                  onColorAxisChange={(checked) =>
                    onChange({ colorAxis: checked ? MatrixAxis.Rows : null })
                  }
                  warningProps={warnings.nodeY}
                  colorAxisTargetId={warnings.colorAxis.targetId}
                />
              )}
              {xNode && (
                <CreateArticleMatrix
                  node={xNode}
                  values={options.nodeXValues ?? []}
                  onChange={(nodeXValues) => onChange({ nodeXValues })}
                  max={30}
                  isColorAxis={options.colorAxis === MatrixAxis.Columns}
                  onColorAxisChange={(checked) =>
                    onChange({ colorAxis: checked ? MatrixAxis.Columns : null })
                  }
                  warningProps={warnings.nodeX}
                  colorAxisTargetId={
                    !yNode ? warnings.colorAxis.targetId : undefined
                  }
                />
              )}
            </Box>
          )}
          {hasMatrix && warnings.colorAxis.warning && (
            <FormControl isInvalid={true}>
              <ArticleFieldWarning {...warnings.colorAxis} />
            </FormControl>
          )}
          {erpConflict && (
            <>
              <Checkbox
                isChecked={!!options.importErp}
                onChange={(e) => onChange({ importErp: e.target.checked })}
              >
                {t('article.create.importErp')}
              </Checkbox>
              {options.importErp && (
                <FormControl
                  isRequired={true}
                  isInvalid={!!warnings.masterBranch.warning}
                  id={warnings.masterBranch.targetId}
                >
                  <TexasFormLabel>
                    {t('article.create.masterBranch')}
                  </TexasFormLabel>

                  <Select
                    aria-label={t('article.create.masterBranch')}
                    placeholder={t('article.create.masterBranch')}
                    value={options.masterBranchId ?? ''}
                    isDisabled={loading}
                    onChange={(e) =>
                      onChange({
                        masterBranchId: Number(e.target.value) || undefined,
                      })
                    }
                  >
                    {(branches ?? []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                  <ArticleFieldWarning {...warnings.masterBranch} />
                </FormControl>
              )}
              {error && <ErrorDetails error={error} />}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
